// src/portal-app/insights/ProductDetailScreen.js
//
// W4 Task 17 — Product Detail P5 rewrite (portal-app-redesign-4-remaining-
// screens). Was: a bare analytics screen (HeroValue + KpiGrid + TrendChart +
// ranks) with no identity block of its own — the nav bar title was the only
// place the product's name appeared, and category/subcategory were squeezed
// into the revenue hero as a "Coffee · Hot" line with nowhere else to live.
//
// Now, following team/EmployeeDetailScreen.js (the reviewed P5 reference):
//   ProfileHero — initials/name/category subtitle.
//     NO status pill: the /products/{code} payload has no status field
//     (verified against reporting_v2.py:1384-1402 — product/period/current/
//     previous/daily_trend/category_rank/overall_rank only). Adding a fetch
//     just to source one would be a new eager call this task doesn't own.
//     NO quick actions: there is no native destination in this app to edit
//     a product from here — the original file already dropped the only
//     candidate ("Open in Product Management", a web-only page) for the
//     same reason; still true, so the hero renders zero actions (a
//     supported ProfileHero state — see ProfileHero.test.js "omits the
//     action row when no actions are given").
//   SegmentedControl (2 options — Performance / Details), not TabPills:
//     spec §5 P5 keeps SegmentedControl for 2-3 segments; only >=4 forces
//     TabPills.
//   Details tab — GroupedRows: Product Code (always set — the query 404s
//     if the code doesn't exist, so this can never collapse) in its own
//     section, then Category/Subcategory in a Classification section that
//     DOES apply the collapsed-empty rule: both are genuinely nullable at
//     the gold layer even though products.category is NOT NULL live —
//     category is MAX(ti.category) off silver.transaction_items (can be
//     null on a historical row) and subcategory is MAX(p.subcategory) via a
//     LEFT JOIN to silver.products, and products.subcategory is
//     nullable=True in models.py:128 (see gold_aggregation.py:530-537).
//     No onAdd — this is a read-only report surface, not a product editor,
//     so EmptyFieldsPrompt renders its sentence with no Add button.
//   Performance tab — the pre-existing analytics zone, governed by the P6
//     rules (spec §5 P6): zone order and charts are UNCHANGED (the hero
//     already carried the GlassCard veil treatment via HeroValue, so no
//     restructure was needed there); no CondensedRow (the ranking card is
//     two backend-composed sentences, not row-shaped data — same
//     "genuinely not applicable" finding Task 14 made for its BarList
//     zones); no ShareBar (units/profit/margin are three independent KPIs,
//     not parts of one whole); PullToRefresh added, wrapping SurfaceStates
//     (not nested inside it) so the pull gesture still works as a retry
//     during the error/empty states, matching the OperationalEfficiency /
//     ProductIntelligence precedent.
//
// Params arrive spread as props from the push:
//   useNav().push('product-detail', { code, name, range: { start, end } })
// (routes.js resolves titleFallback with `name`, so NavStackHost's header
// already shows the product name — duplicated in ProfileHero below, same
// as EmployeeDetailScreen duplicates the employee's name between the nav
// bar and its own hero; that duplication is the established P5 pattern,
// not an oversight.)
//
// Data — reused UNCHANGED from services/api.js:
//   reportingService.getProductDetail(code, start, end)
//     → { product, period, current, previous, daily_trend[30],
//         category_rank, overall_rank }
//     → 404 when the code has no rows for the tenant — a DISTINCT
//       not-found state (product copy, no dead Retry), separate from error
//       (retryable) and empty (zero activity in the period).
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GlassCard } from '../shell/LiquidGlass';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { reportingService } from '../../services/api';
import { rangeForPreset, toLocalISO, trendLabel } from '../nav/dateRange';
import {
    normaliseKpi,
    fmtMoney,
    HeroValue,
    KpiGrid,
    TrendChart,
    SurfaceStates,
} from './kit';
import {
    ProfileHero, SegmentedControl, GroupedRows, EmptyFieldsPrompt,
    PullToRefresh, ensureKitCss, FALLBACK_TONES,
} from '../shell/kit';

/* Screen-local chrome only — everything data-bearing comes from the kit
   (the analytics anatomy is the kit's HeroValue/KpiGrid/TrendChart; the P5
   identity/fields anatomy is shell/kit's ProfileHero/GroupedRows/
   EmptyFieldsPrompt). */
const CSS = `
.pds-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;}
.pds-hero{padding-inline:16px;margin-block-end:16px;}
.pds-tabsrow{padding-inline:16px;margin-block-end:16px;}
.pds-shead{padding-inline:16px;margin-block:18px 8px;}
.pds-shead-title{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-textLight);}
.pds-block{padding-inline:16px;margin-block-end:12px;}
.pds-ranks{padding:16px 18px;margin-bottom:14px;}
.pds-rank-line{margin-top:8px;font-size:13px;font-weight:600;color:var(--c-ink);}
`;

const TAB_IDS = {
    PERFORMANCE: 'performance',
    DETAILS: 'details',
};

/* The web detail modal's tile delta formula (ProductIntelligencePage.js
   ~1827): prev ? ((cur − prev) / |prev|) × 100 : null. Reproduced exactly —
   including for margin, where the web divides percents (a relative delta,
   NOT points). */
function pctDelta(cur, prev) {
    return prev ? ((cur - prev) / Math.abs(prev)) * 100 : null;
}

/* Build a _build_kpi-shaped object so normaliseKpi/KpiGrid/DeltaPill absorb
   formatting exactly as on the list surfaces. */
function kpiShape(name, cur, prev, extra = {}) {
    const c = Number(cur) || 0;
    const p = Number(prev) || 0;
    const change = pctDelta(c, p);
    return {
        name,
        value: c,
        previous_value: prev == null ? null : p,
        change_percent: change == null ? null : Math.round(change * 10) / 10,
        change_absolute: c - p,
        invert: false,
        ...extra,
    };
}

/* "Empty" = the product exists but produced nothing in this period AND
   shows no activity across the 30-day trend window. */
function isEmptyPayload(d) {
    if (!d) return true;
    const cur = d.current || {};
    const hasCurrent = Number(cur.units) || Number(cur.revenue);
    const hasTrend = (d.daily_trend || []).some((r) => Number(r.revenue) || Number(r.units));
    return !(hasCurrent || hasTrend);
}

/* ------------------------------------------------------------------ */
/* Identity helpers — ProfileHero initials/tone. Same two-letter/hash      */
/* convention as team/EmployeeDetailScreen.js's initialsFromName /          */
/* heroToneFromName, kept as a local copy rather than an import: this is    */
/* the only other P5 screen and the two are not meant to be coupled.        */
/* ------------------------------------------------------------------ */
function initialsFromName(name = '') {
    return name.trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();
}

function toneFromName(name = '') {
    const code = name.charCodeAt(0) || 0;
    return FALLBACK_TONES[code % FALLBACK_TONES.length];
}

/* ------------------------------------------------------------------ */
/* SectionHead — title only. Product fields have no per-field edit sheet    */
/* (read-only report surface), so unlike EmployeeDetailScreen's SectionHead */
/* there is no onEdit slot to carry.                                        */
/* ------------------------------------------------------------------ */
function SectionHead({ title }) {
    return (
        <div className="pds-shead">
            <span className="pds-shead-title">{title}</span>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* FieldBlock — the P5 collapse rule. Partitions `fields` into set/unset.   */
/* Set ones render as GroupedRows; every unset one collapses into ONE       */
/* EmptyFieldsPrompt. All-unset -> prompt only, no empty card above it.     */
/* None-unset -> no prompt. No onAdd is ever passed — see file header.      */
/* ------------------------------------------------------------------ */
function FieldBlock({ fields }) {
    const set = fields.filter((f) => f.value);
    const unset = fields.filter((f) => !f.value);
    const rows = set.map((f) => ({ id: f.key, label: f.label, value: f.value }));

    if (rows.length === 0 && unset.length === 0) return null;

    return (
        <div className="pds-block">
            {rows.length > 0 && <GroupedRows rows={rows} />}
            {unset.length > 0 && (
                <EmptyFieldsPrompt fields={unset.map((f) => f.label)} />
            )}
        </div>
    );
}

export default function ProductDetailScreen({ code, name, range }) {
    ensureKitCss();
    const auth = useAuth() || {};
    const currency = auth.getCurrency ? auth.getCurrency() : '£';
    const { t, language, isRTL } = useLanguage();

    /* The pushed range is already local-time ISO; a deep entry without one
       falls back to the week preset (same default as the list surface). */
    const week = rangeForPreset('week');
    const startIso = (range && range.start) || toLocalISO(week.start);
    const endIso = (range && range.end) || toLocalISO(week.end);

    // loading | error | notfound | empty | ready
    const [status, setStatus] = useState('loading');
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState(TAB_IDS.PERFORMANCE);

    const seqRef = useRef(0);
    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');
        // Returned so PullToRefresh can await it — its spinner stays up
        // until the fetch actually settles, success or failure.
        return reportingService.getProductDetail(code, startIso, endIso)
            .then((d) => {
                if (seq !== seqRef.current) return;
                setData(d);
                setStatus(isEmptyPayload(d) ? 'empty' : 'ready');
            })
            .catch((err) => {
                if (seq !== seqRef.current) return;
                const httpStatus = err && err.response && err.response.status;
                setStatus(httpStatus === 404 ? 'notfound' : 'error');
            });
    }, [code, startIso, endIso]);
    useEffect(() => { load(); }, [load]);

    const product = (data && data.product) || {};
    const current = (data && data.current) || {};
    const previous = (data && data.previous) || {};

    /* ---- hero: revenue + delta (web modal delta formula; rendering is
       the kit's HeroValue) ---- */
    const revNorm = normaliseKpi(kpiShape('Revenue', current.revenue, previous.revenue));

    /* ---- tiles: units / profit / margin (odd count → last spans) ---- */
    const gridItems = [
        {
            kpi: kpiShape('Units', current.units, previous.units),
            label: t('table_units', {}, 'Units'),
            format: 'number',
            caption: true,
        },
        {
            kpi: kpiShape('Profit', current.profit, previous.profit),
            label: t('kpi_profit', {}, 'Profit'),
            format: 'currency',
            caption: true,
        },
        {
            kpi: kpiShape('Gross Margin', current.margin_pct, previous.margin_pct, { unit: '%' }),
            label: t('kpi_gross_margin', {}, 'Gross Margin'),
            format: 'number',
            caption: true,
            span: 2,
        },
    ];

    /* ---- 30-day trend: backend zero-fills all 30 days, so no gap
       handling is needed here; labels via trendLabel (local-noon parse —
       never new Date('YYYY-MM-DD')). The row key is `date`, mapped to the
       `sales_date` key trendLabel expects. ---- */
    const trendPoints = ((data && data.daily_trend) || []).map((r) => ({
        label: trendLabel({ sales_date: r.date }, 'daily', language),
        value: Number(r.revenue) || 0,
    }));

    /* ---- ranks: backend-composed strings ("2 of 8 in Coffee") ---- */
    const rankLines = data
        ? [data.category_rank, data.overall_rank].filter(Boolean)
        : [];

    /* ---- Details tab: Product Info (always-set Code) + Classification
       (Category/Subcategory — the genuinely nullable pair, see file
       header for the gold-layer verification). ---- */
    const identityFields = [
        { key: 'code', label: t('product_code', {}, 'Product Code'), value: product.code },
    ];
    const classificationFields = [
        { key: 'category', label: t('category', {}, 'Category'), value: product.category },
        { key: 'subcategory', label: t('subcategory', {}, 'Subcategory'), value: product.subcategory },
    ];

    /* ---- Tabs (SegmentedControl — 2 options, never TabPills territory) ---- */
    const tabOptions = [
        { id: TAB_IDS.PERFORMANCE, label: t('tab_performance', {}, 'Performance') },
        { id: TAB_IDS.DETAILS, label: t('tab_details', {}, 'Details') },
    ];

    const isNotFound = status === 'notfound';
    return (
        <div
            className="pds-wrap"
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="product-detail-screen"
        >
            <style>{CSS}</style>
            <PullToRefresh onRefresh={load}>
                <SurfaceStates
                    status={isNotFound ? 'empty' : status}
                    onRetry={load}
                    emptyTitle={isNotFound
                        ? t('product_not_found_title', {}, 'Product not found')
                        : undefined}
                    emptyMsg={isNotFound
                        ? t('product_not_found_msg', {}, 'This product has no sales records for your business.')
                        : undefined}
                >
                    {data && (
                        <>
                            {/* Identity block — a detail screen without one could be
                                about anyone; this is what makes it about a product. */}
                            <div className="pds-hero">
                                <ProfileHero
                                    initials={initialsFromName(product.name || name || '')}
                                    tone={toneFromName(product.name || name || '')}
                                    name={product.name || name}
                                    subtitle={product.category || undefined}
                                    actions={[]}
                                />
                            </div>

                            {/* Section tabs */}
                            <div className="pds-tabsrow">
                                <SegmentedControl
                                    options={tabOptions}
                                    value={activeTab}
                                    onChange={setActiveTab}
                                />
                            </div>

                            {/* Details tab — Product Info fields */}
                            {activeTab === TAB_IDS.DETAILS && (
                                <div>
                                    <SectionHead title={t('product_info', {}, 'Product Info')} />
                                    <FieldBlock fields={identityFields} />

                                    <SectionHead title={t('classification', {}, 'Classification')} />
                                    <FieldBlock fields={classificationFields} />
                                </div>
                            )}

                            {/* Performance tab — pre-existing analytics zone (P6 rules) */}
                            {activeTab === TAB_IDS.PERFORMANCE && (
                                <div>
                                    <HeroValue
                                        label={t('revenue', {}, 'Revenue')}
                                        norm={revNorm}
                                        currency={currency}
                                        testid="pd-hero"
                                    />

                                    {/* Units / Profit / Margin tiles. */}
                                    <KpiGrid currency={currency} items={gridItems} />

                                    {/* 30-day revenue trend (zero-filled by the backend). */}
                                    <TrendChart
                                        title={t('thirty_day_trend', {}, '30-Day Trend')}
                                        /* aria-label carries count context beyond the visible
                                           title (Task-5 canonical pattern), reusing the
                                           already-registered trend_points_count key. */
                                        ariaLabel={`${t('thirty_day_trend', {}, '30-Day Trend')} — ${t('trend_points_count', { count: trendPoints.length }, '{count} data points')}`}
                                        points={trendPoints}
                                        formatValue={(v) => fmtMoney(v, currency)}
                                    />

                                    {/* Ranking — backend-composed strings, rendered verbatim. */}
                                    {rankLines.length > 0 && (
                                        <GlassCard veil className="pds-ranks" data-testid="pd-ranks">
                                            <div className="insk-label">
                                                {t('ranking', {}, 'Ranking')}
                                            </div>
                                            {rankLines.map((line) => (
                                                <div className="pds-rank-line insk-num" key={line}>
                                                    {line}
                                                </div>
                                            ))}
                                        </GlassCard>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </SurfaceStates>
            </PullToRefresh>
        </div>
    );
}
